const STATUS_LABELS = {
  ok: 'OK',
  missing: 'Missing',
  denied: 'Denied',
  skipped: 'Blocked',
  error: 'Error',
};

const STATUS_CLASS = {
  ok: 'integration-req-ok',
  missing: 'integration-req-missing',
  denied: 'integration-req-denied',
  skipped: 'integration-req-skipped',
  error: 'integration-req-error',
};

const KIND_LABELS = {
  credential: 'Credential',
  config: 'Setting',
  scope: 'Scope',
  permission: 'Permission',
};

function requirementStatusLabel(row) {
  return STATUS_LABELS[row.status] ?? row.status;
}

function portalFeatures(result) {
  return result?.portal_features ?? result?.requirements_summary?.portal_features ?? { available: [], unavailable: [] };
}

export function integrationStatusBadge(configured, health) {
  if (!configured) {
    return { text: 'Not configured', className: 'text-bg-secondary' };
  }
  if (!health) {
    return { text: 'Configured', className: 'text-bg-secondary' };
  }
  if (health.loading) {
    return { text: 'Checking…', className: 'text-bg-secondary' };
  }
  if (health.fully_ready) {
    return { text: 'Ready', className: 'text-bg-success' };
  }
  if (health.ok) {
    return { text: 'Partial', className: 'badge-warn' };
  }
  return { text: 'Needs attention', className: 'badge-warn' };
}

function FeatureList({ title, items, variant }) {
  if (!items?.length) {
    return null;
  }

  return (
    <div className={`integration-features-group integration-features-${variant}`}>
      <p className="integration-features-title">{title}</p>
      <ul className="integration-features-list">
        {items.map((item) => (
          <li key={item.id}>{item.label}</li>
        ))}
      </ul>
    </div>
  );
}

function RequirementFeatureHint({ row }) {
  if (row.blocks?.length) {
    return (
      <div className="integration-req-impact integration-req-impact-blocked">
        <strong>Unavailable in portal:</strong> {row.blocks.join(' · ')}
      </div>
    );
  }

  if (row.supports?.length) {
    return (
      <div className="integration-req-impact integration-req-impact-enabled">
        <strong>Available in portal:</strong> {row.supports.join(' · ')}
      </div>
    );
  }

  return null;
}

export function IntegrationRequirementsPanel({ result, loading, error }) {
  if (loading) {
    return <p className="muted small integration-requirements">Checking permissions and settings…</p>;
  }

  if (error) {
    return (
      <div className="integration-requirements">
        <p className="err small">{error}</p>
      </div>
    );
  }

  if (!result?.requirements?.length) {
    return null;
  }

  const summary = result.requirements_summary;
  const features = portalFeatures(result);
  const required = result.requirements.filter((row) => row.required);
  const optional = result.requirements.filter((row) => !row.required);

  return (
    <div className="integration-requirements">
      <div className="integration-requirements-head">
        <strong>Integration readiness</strong>
        <span className={`badge ${result.fully_ready ? 'badge-ok' : 'badge-warn'}`}>
          {result.fully_ready
            ? 'All required checks passed'
            : `${summary?.required_ok ?? 0}/${summary?.required_total ?? required.length} required`}
        </span>
      </div>

      {features.available.length || features.unavailable.length ? (
        <div className="integration-features-summary">
          <FeatureList title="Available now" items={features.available} variant="available" />
          {features.unavailable.length ? (
            <div className="integration-features-group integration-features-unavailable">
              <p className="integration-features-title">Not available yet</p>
              <ul className="integration-features-list">
                {features.unavailable.map((item) => (
                  <li key={item.id}>
                    <span>{item.label}</span>
                    {item.blocked_by?.length ? (
                      <span className="integration-features-blockers muted small">
                        Needs: {item.blocked_by.map((blocker) => blocker.label).join(', ')}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {result.message && !result.ok ? <p className="err small">{result.message}</p> : null}

      <ul className="integration-requirements-list">
        {required.map((row) => (
          <li key={row.id} className={`integration-requirements-item ${STATUS_CLASS[row.status] ?? ''}`}>
            <span className={`integration-req-dot ${STATUS_CLASS[row.status] ?? ''}`} aria-hidden />
            <div className="integration-requirements-body">
              <div className="integration-requirements-title">
                <span>{row.label}</span>
                <span className="integration-requirements-meta">
                  {KIND_LABELS[row.kind] ?? row.kind}
                  {row.id && row.kind === 'scope' ? ` · ${row.id}` : null}
                </span>
              </div>
              <div className="integration-requirements-status">{requirementStatusLabel(row)}</div>
              {row.message ? <div className="muted small">{row.message}</div> : null}
              <RequirementFeatureHint row={row} />
            </div>
          </li>
        ))}
      </ul>

      {optional.length ? (
        <>
          <p className="muted small integration-requirements-optional-label">Optional</p>
          <ul className="integration-requirements-list integration-requirements-list-optional">
            {optional.map((row) => (
              <li key={row.id} className={`integration-requirements-item ${STATUS_CLASS[row.status] ?? ''}`}>
                <span className={`integration-req-dot ${STATUS_CLASS[row.status] ?? ''}`} aria-hidden />
                <div className="integration-requirements-body">
                  <div className="integration-requirements-title">
                    <span>{row.label}</span>
                    <span className="integration-requirements-meta">{KIND_LABELS[row.kind] ?? row.kind}</span>
                  </div>
                  <div className="integration-requirements-status">{requirementStatusLabel(row)}</div>
                  {row.message ? <div className="muted small">{row.message}</div> : null}
                  <RequirementFeatureHint row={row} />
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
